import { getStore, RootState } from '@store';
import { Type } from 'class-transformer';
import { Organization } from '@services/organization';
import { authService } from '@services/auth';
import { Role } from '@services/role/role.class';
import { Permission } from '@services/role/permission.enum';
import { PredefinedRole } from '@services/role/predefined-role.enum';

const store = getStore().store;

export class User {
	email: string;
	name: string;
	surname: string;

	@Type(() => Organization)
	organization?: Organization;

	@Type(() => Role)
	role?: Role;

	roleId?: number;
	termsAccepted?: boolean;

	get assumedOrganization(): Maybe<Organization> {
		return (store.getState() as RootState).Portal.assumedOrganization;
	}

	get assumedRole(): Maybe<Role> {
		return (store.getState() as RootState).Portal.assumedRole;
	}

	isB2C = () => this.organization?.isB2CSeller || false;
	isInfraSeller = () => this.organization?.isInfraSeller || false;
	isVehicleSeller = () => this.organization?.isVehicleSeller || false;
	isHybridSeller = () => this.isInfraSeller() && this.isVehicleSeller();
	isAdmin = () => authService.isAdmin();

	isRoleSwitched = () => !!this.assumedRole;
	isDealer = (role = this.assumedRole || this.role) => role?.name === PredefinedRole.DEALER;
	isChargingPartner = (role = this.assumedRole || this.role) => role?.name === PredefinedRole.CHARGING_PARTNER;
	isFleetManager = (role = this.assumedRole || this.role) => role?.name === PredefinedRole.FLEET_MANAGER;
	isFacilityManager = (role = this.assumedRole || this.role) => role?.name === PredefinedRole.FACILITY_MANAGER;
	isDriver = (role = this.assumedRole || this.role) => role?.name === PredefinedRole.DRIVER;
	isSeller = (role = this.assumedRole || this.role) => this.isDealer(role) || this.isChargingPartner(role);
	isManager = (role = this.assumedRole || this.role) => this.isFleetManager(role) || this.isFacilityManager(role);
	isSellerLoggedInAsManager = () =>
		!!(this.isDealer(this.role) && this.isRoleSwitched() && this.isFleetManager()) ||
		!!(this?.isChargingPartner(this.role) && this.isRoleSwitched() && this.isFacilityManager());

	getAvatarLetters = () => (this.name && this.surname ? [this.name[0], this.surname[0]] : [this.email[0]]).join('').toUpperCase();
	canManageCollaborators = () => this.hasPermission([Permission.USER_WRITE, Permission.INVITE_USER, Permission.ROLE_WRITE]);
	canManageRole = ({ name }: Role) => {
		if (this.isFleetManager()) {
			return ![PredefinedRole.DEALER].includes(name as PredefinedRole);
		} else if (this.isDriver()) {
			return ![PredefinedRole.DEALER, PredefinedRole.FLEET_MANAGER].includes(name as PredefinedRole);
		}

		return true;
	};

	hasPermission(requiredPermission?: Permission | Permission[], type: 'or' | 'and' = 'or') {
		if (requiredPermission?.length) {
			const permToCheck = (Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission]).filter(Boolean);
			const userPermissions = this.assumedRole?.permissions || this.role?.permissions || [];
			if (type === 'and') {
				return !permToCheck.some((perm) => !userPermissions.includes(perm));
			} else {
				return permToCheck.some((perm) => userPermissions.includes(perm));
			}
		} else {
			return true;
		}
	}
}
